'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { Info, Link, ArrowRight, Database, Table } from '@phosphor-icons/react';

interface JoinConfigProps {
  nodeId: string;
  config: {
    leftKey?: string;
    rightKey?: string;
    joinType?: 'left' | 'inner' | 'right' | 'full';
  };
}

const joinTypes = [
  { 
    value: 'left', 
    label: '左连接', 
    sublabel: 'LEFT JOIN',
    description: '保留左表所有记录，右表不匹配的记录填充 NULL',
    color: 'bg-blue-50 border-blue-200 text-blue-700'
  },
  { 
    value: 'inner', 
    label: '内连接', 
    sublabel: 'INNER JOIN',
    description: '只保留两个表都匹配的记录',
    color: 'bg-emerald-50 border-emerald-200 text-emerald-700'
  },
  { 
    value: 'right', 
    label: '右连接', 
    sublabel: 'RIGHT JOIN',
    description: '保留右表所有记录，左表不匹配的记录填充 NULL',
    color: 'bg-purple-50 border-purple-200 text-purple-700'
  },
  { 
    value: 'full', 
    label: '全连接', 
    sublabel: 'FULL JOIN',
    description: '保留两个表的所有记录，不匹配的地方填充 NULL',
    color: 'bg-amber-50 border-amber-200 text-amber-700'
  },
];

// 输入列选择器组件
interface ColumnSelectorProps {
  value: string;
  onChange: (columnName: string) => void;
  columns: { column: { name: string; type: string }; source: { nodeId: string; nodeType: string; nodeName: string } }[] | undefined;
  placeholder?: string;
  label: string;
}

function InputColumnSelector({ value, onChange, columns, placeholder = '选择字段...' }: ColumnSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!columns || columns.length === 0) {
    return (
      <div className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm text-zinc-400">
        请先连接上游节点
      </div>
    );
  }

  const selectedColumn = columns.find(({ column }) => column.name === value);
  const sourceName = columns[0]?.source.nodeName || '未知来源';
  const isDataSource = columns[0]?.source.nodeType === 'data_source';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between px-3 py-2 
          bg-white border rounded-lg text-sm
          transition-all duration-200
          ${isOpen 
            ? 'border-emerald-500 ring-2 ring-emerald-500/20' 
            : 'border-zinc-200 hover:border-zinc-300'
          }
        `}
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedColumn ? (
            <>
              <span className="font-medium text-zinc-900 truncate">
                {selectedColumn.column.name}
              </span>
              <span className="text-xs text-zinc-400">
                ({selectedColumn.column.type})
              </span>
            </>
          ) : (
            <span className="text-zinc-400">{placeholder}</span>
          )}
        </div>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Panel */}
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="absolute top-full left-0 right-0 mt-2 z-50 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 overflow-hidden"
          >
            {/* Source Header */}
            <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
              {isDataSource ? (
                <Database className="w-3.5 h-3.5 text-emerald-500" />
              ) : (
                <Table className="w-3.5 h-3.5 text-blue-500" />
              )}
              <span className="text-xs font-medium text-zinc-600 truncate">
                {sourceName}
              </span>
            </div>

            {/* Column List */}
            <div className="max-h-48 overflow-y-auto p-1">
              {columns.map(({ column }) => (
                <button
                  key={column.name}
                  type="button"
                  onClick={() => {
                    onChange(column.name);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm
                    transition-colors
                    ${value === column.name
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'hover:bg-zinc-50 text-zinc-700'
                    }
                  `}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium truncate">
                      {column.name}
                    </span>
                    <span className="text-xs text-zinc-400">
                      ({column.type})
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Footer */}
            <div className="px-3 py-2 bg-zinc-50 border-t border-zinc-100 text-xs text-zinc-400">
              共 {columns.length} 个字段
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}

export function JoinConfig({ nodeId, config }: JoinConfigProps) {
  const { updateNodeConfig } = useWorkflowStore();
  const [leftKey, setLeftKey] = useState(config.leftKey || '');
  const [rightKey, setRightKey] = useState(config.rightKey || '');
  const [joinType, setJoinType] = useState(config.joinType || 'left');

  // 获取左表（第一个输入）和右表（第二个输入）的列
  const leftColumns = useNodeInputColumns(nodeId, 0);
  const rightColumns = useNodeInputColumns(nodeId, 1);

  const updateConfig = (updates: { leftKey?: string; rightKey?: string; joinType?: string }) => {
    const newConfig = {
      type: 'join',
      params: {
        leftKey: updates.leftKey ?? leftKey,
        rightKey: updates.rightKey ?? rightKey,
        joinType: updates.joinType ?? joinType,
      },
    };
    updateNodeConfig(nodeId, newConfig);
  };

  const selectedType = joinTypes.find((t) => t.value === joinType);

  return (
    <div className="space-y-5">
      <div className="text-sm font-medium text-zinc-700">连接配置</div>
      
      {/* Join Type Selection */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">连接类型</label>
        <div className="grid grid-cols-2 gap-2">
          {joinTypes.map((type) => (
            <motion.button
              key={type.value}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setJoinType(type.value as typeof joinType);
                updateConfig({ joinType: type.value });
              }}
              className={`
                p-3 rounded-xl border text-left transition-all
                ${joinType === type.value
                  ? type.color
                  : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">{type.label}</span>
                <span className="text-xs opacity-70">{type.sublabel}</span>
              </div>
            </motion.button>
          ))}
        </div>
        
        {/* Selected Type Description */}
        <motion.div
          key={joinType}
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-zinc-50 rounded-lg text-xs text-zinc-600"
        >
          {selectedType?.description}
        </motion.div>
      </div>

      {/* Join Keys */}
      <div className="space-y-4">
        <label className="text-xs font-medium text-zinc-500">关联键配置</label>
        
        <div className="grid grid-cols-[1fr,auto,1fr] gap-3 items-center">
          {/* Left Key */}
          <div className="space-y-1.5">
            <span className="text-xs text-zinc-400">左表字段</span>
            <InputColumnSelector
              value={leftKey}
              onChange={(columnName) => {
                setLeftKey(columnName);
                updateConfig({ leftKey: columnName });
              }}
              columns={leftColumns}
              placeholder="选择字段..."
              label="左表字段"
            />
          </div>

          {/* Connector */}
          <div className="flex items-center justify-center pt-5">
            <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center">
              <Link className="w-4 h-4 text-zinc-400" />
            </div>
          </div>

          {/* Right Key */}
          <div className="space-y-1.5">
            <span className="text-xs text-zinc-400">右表字段</span>
            <InputColumnSelector
              value={rightKey}
              onChange={(columnName) => {
                setRightKey(columnName);
                updateConfig({ rightKey: columnName });
              }}
              columns={rightColumns}
              placeholder="选择字段..."
              label="右表字段"
            />
          </div>
        </div>

        {/* Auto-match suggestion */}
        {leftKey && !rightKey && rightColumns?.some(col => col.column.name === leftKey) && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => {
              setRightKey(leftKey);
              updateConfig({ rightKey: leftKey });
            }}
            className="flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700"
          >
            <ArrowRight className="w-3.5 h-3.5" />
            右表存在相同字段名，点击使用
          </motion.button>
        )}
      </div>

      {/* Info Tip */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700 leading-relaxed">
          关联键用于匹配两个表中的记录。确保选择的列在两个表中都存在且具有相同的含义，例如用户ID或订单号。
        </p>
      </div>
    </div>
  );
}
